import { useMemo } from "react";

import { LABELLING } from "../config/labelling";
import { useRegionalBackend } from "../context/RegionalBackendContext";
import { resolveShowFullResults } from "../config/results";
import {
  resolveUserWebAppElementFeatures,
  resolveUserWebAppMiscFeatures,
  resolveUserWebAppQualityStatusPills,
  resolveUserWebAppResultFeatures,
} from "../utils/productPresentation/resultFeatures";

function isEnabled(value) {
  return ["1", "true", "yes", "on"].includes(
    String(value || "")
      .trim()
      .toLowerCase(),
  );
}

export const DEMO_MODE_ENABLED = isEnabled(process.env.REACT_APP_DEMO_MODE);

export function resolveDemoModeEnabled(productPresentationProfile) {
  const explicitValue =
    productPresentationProfile?.userWebApp?.misc?.demoModeEnabled;

  return typeof explicitValue === "boolean"
    ? explicitValue
    : DEMO_MODE_ENABLED;
}

export function resolveWeatherDetailsEnabled(
  productPresentationProfile,
  userWebAppFeatureAccess = null,
) {
  return (
    resolveUserWebAppMiscFeatures(
      productPresentationProfile,
      userWebAppFeatureAccess,
    ).weatherDetails === true
  );
}

export function useUserWebAppPresentationFeatures() {
  const { productPresentationProfile, recordingModeConfig } = useRegionalBackend();
  const userWebAppFeatureAccess = recordingModeConfig?.userWebAppFeatureAccess;

  return useMemo(
    () => {
      const resultFeatures =
        resolveUserWebAppResultFeatures(
          productPresentationProfile,
          userWebAppFeatureAccess,
        );
      const elementFeatures =
        resolveUserWebAppElementFeatures(
          productPresentationProfile,
          userWebAppFeatureAccess,
        );
      const miscFeatures =
        resolveUserWebAppMiscFeatures(
          productPresentationProfile,
          userWebAppFeatureAccess,
        );
      const qualityStatusPillsEnabled = resolveUserWebAppQualityStatusPills(
        productPresentationProfile,
        LABELLING.PRODUCT_PRESENTATION_PROFILE_ID,
      );

      return {
        showFullResults: resolveShowFullResults(productPresentationProfile),
        demoModeEnabled: resolveDemoModeEnabled(productPresentationProfile),
        weatherDetailsEnabled: miscFeatures.weatherDetails === true,
        qualityStatusPillsEnabled,
        resultFeatures,
        elementFeatures,
        canViewResult: (featureKey) => Boolean(resultFeatures?.[featureKey]),
        canViewElement: (featureKey) => Boolean(elementFeatures?.[featureKey]),
      };
    },
    [productPresentationProfile, userWebAppFeatureAccess],
  );
}

export default useUserWebAppPresentationFeatures;
